// Quadruples: a white circle with an ink border on the corner the cells share, the digits in
// small text inside, or the setter's own lines as written ("2?" over "3?": Endeavour). Pack 9: a parity circle (`same-parity`) is the same circle, empty; a
// Battenburg marking a small square of the same size and border, its quarters white and
// thermo grey like a checkerboard.
import type { PuzzleDoc } from '@zinc/shared';
import { CELL, ofType, rcList, sharedCorner, styleOf } from './geom';
import { FONT, INK, LINES, QUAD, WHITE } from './style';

/** Digit positions in the circle: one centred, two side by side, three or four in a square. */
function slots(n: number): { x: number; y: number; size: number }[] {
  if (n <= 1) return [{ x: 0, y: 0, size: 12 }];
  if (n === 2)
    return [
      { x: -4.2, y: 0, size: 10 },
      { x: 4.2, y: 0, size: 10 },
    ];
  const d = 4.4;
  const four = [
    { x: -d, y: -d, size: 8.5 },
    { x: d, y: -d, size: 8.5 },
    { x: -d, y: d, size: 8.5 },
    { x: d, y: d, size: 8.5 },
  ];
  return n === 3 ? [four[0]!, four[1]!, { x: 0, y: d, size: 8.5 }] : four;
}

/** Positions for the setter's lines, top to bottom, each line's characters left to right. */
function lineSlots(lines: string[]): { x: number; y: number; size: number; ch: string }[] {
  if (lines.length === 1) {
    const chars = [...lines[0]!];
    return slots(chars.length).map((s, k) => ({ ...s, ch: chars[k]! }));
  }
  const rowGap = lines.length === 2 ? 8.8 : 6.5;
  const out: { x: number; y: number; size: number; ch: string }[] = [];
  lines.forEach((line, i) => {
    const chars = [...line];
    const y = (i - (lines.length - 1) / 2) * rowGap;
    const [size, gap] =
      lines.length > 2
        ? [6.5, 5]
        : chars.length <= 2
          ? [8.5, 8.8]
          : chars.length === 3
            ? [7.5, 6.2]
            : [6.5, 4.8];
    chars.forEach((ch, k) => out.push({ x: (k - (chars.length - 1) / 2) * gap, y, size, ch }));
  });
  return out;
}

/** The setter's lines when they still say the clue's digits (an edited clue drops them). */
function setterLines(text: unknown, digits: string[]): string[] | null {
  if (!Array.isArray(text) || text.length === 0) return null;
  const lines = text.map(String);
  const said = lines.join('').replace(/\?/g, '');
  return said === digits.join('') ? lines : null;
}

function Battenburgs({ doc }: { doc: PuzzleDoc }) {
  const h = QUAD.radius * 0.8;
  return (
    <g>
      {ofType(doc, 'battenburg').map((e, i) => {
        const corner = sharedCorner(rcList(e), doc.grid.rows, doc.grid.cols);
        if (!corner || e.negative === true) return null;
        const cx = corner[1] * CELL;
        const cy = corner[0] * CELL;
        return (
          <g key={i} data-constraint="battenburg">
            <rect x={cx - h} y={cy - h} width={h * 2} height={h * 2} fill={WHITE} />
            <rect x={cx} y={cy - h} width={h} height={h} fill={LINES.thermo.color} />
            <rect x={cx - h} y={cy} width={h} height={h} fill={LINES.thermo.color} />
            <rect
              x={cx - h}
              y={cy - h}
              width={h * 2}
              height={h * 2}
              fill="none"
              stroke={INK}
              strokeWidth={QUAD.strokeWidth}
            />
          </g>
        );
      })}
    </g>
  );
}

export function Quadruples({ doc }: { doc: PuzzleDoc }) {
  return (
    <g>
      <Battenburgs doc={doc} />
      {ofType(doc, 'quadruple', 'wheel', 'same-parity').map((e, i) => {
        const corner = sharedCorner(rcList(e), doc.grid.rows, doc.grid.cols);
        if (!corner) return null;
        let digits = Array.isArray(e.digits) ? e.digits.map(String).slice(0, 4) : [];
        const lines = e.type === 'quadruple' ? setterLines(e.text, digits) : null;
        // A wheel lists its digits clockwise from the top left; the slots read left to right.
        if (e.type === 'wheel' && digits.length === 4)
          digits = [digits[0]!, digits[1]!, digits[3]!, digits[2]!];
        const wheel = e.type === 'wheel';
        const cx = corner[1] * CELL;
        const cy = corner[0] * CELL;
        const { color } = styleOf(e, { color: INK, width: QUAD.strokeWidth });
        return (
          <g key={i} data-constraint={e.type}>
            <circle
              cx={cx}
              cy={cy}
              r={QUAD.radius}
              fill={wheel ? LINES.thermo.color : WHITE}
              stroke={color}
              strokeWidth={QUAD.strokeWidth}
            />
            {(lines
              ? lineSlots(lines)
              : (digits.length > 0 ? slots(digits.length) : []).map((s, k) => ({
                  ...s,
                  ch: digits[k]!,
                }))
            ).map((s, k) => (
              <text
                key={k}
                x={cx + s.x}
                y={cy + s.y + 0.5}
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily={FONT}
                fontSize={s.size}
                fontWeight={600}
                fill={INK}
              >
                {s.ch}
              </text>
            ))}
          </g>
        );
      })}
    </g>
  );
}
